/*
 * Iter 31.298 — SpyRadio single-station deep-link page.
 *
 * Six's brief: the multi-station player at `/` on spyradio.uk lists
 * every station in a left column with the currently-selected station's
 * player on the right. This page is the same PlayerPane but on its
 * OWN URL — one station per page, shareable, indexable, browser-
 * back-friendly.
 *
 * Routes registered in App.js:
 *   spyradio.uk/s/<slug>                     (public prod)
 *   spyfinance.cloud/spyradio/live/s/<slug>  (preview mirror)
 *
 * The multi-station overview at `/` is preserved unchanged — this
 * page is purely additive per Six's "best of both" call.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";

import {
    COLOURS, useDisplayFonts, StationCard, PlayerPane, TrackList,
} from "./SpyRadioMultiStation";

const API = process.env.REACT_APP_BACKEND_URL;

// Resolve /s/<slug> both when the CF Worker rewrites the request AND
// when serving directly on spyradio.uk (React Router param). Fall
// back to reading window.location if the slug param is missing.
function useSlug() {
    const { slug } = useParams();
    if (slug) return slug;
    try {
        const parts = (window.location.pathname || "").split("/").filter(Boolean);
        const idx = parts.indexOf("s");
        return idx >= 0 && parts[idx + 1] ? parts[idx + 1] : null;
    } catch {
        return null;
    }
}

export default function SpyRadioStation() {
    useDisplayFonts();
    const slug = useSlug();
    const [station, setStation] = useState(null);
    const [stations, setStations] = useState([]);
    const [tracks, setTracks] = useState(null);
    const [tracksLoading, setTracksLoading] = useState(true);
    const [err, setErr] = useState("");

    // Fetch the full station list so we can (a) resolve slug → station
    // metadata and (b) render sibling stations at the bottom.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${API}/api/spyradio/public/stations`);
                if (cancelled) return;
                const list = data.stations || [];
                setStations(list);
                const match = list.find((s) => s.slug === slug);
                if (match) setStation(match);
                else setErr(`Station "${slug}" not found.`);
            } catch (e) {
                if (!cancelled) setErr(e?.message || "load failed");
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    // Fetch tracks for this specific slug.
    useEffect(() => {
        if (!slug) return;
        let cancelled = false;
        setTracksLoading(true);
        (async () => {
            try {
                const { data } = await axios.get(
                    `${API}/api/spyradio/public/stations/${encodeURIComponent(slug)}/tracks`,
                );
                if (cancelled) return;
                setTracks(data.tracks || []);
            } catch {
                if (!cancelled) setTracks([]);
            } finally {
                if (!cancelled) setTracksLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [slug]);

    const siblings = (stations || []).filter((s) => s.slug !== slug);

    return (
        <div
            style={{
                background: COLOURS.bg,
                minHeight: "100vh",
                color: COLOURS.ink,
                fontFamily: "'Lexend', system-ui, sans-serif",
                WebkitFontSmoothing: "antialiased",
                MozOsxFontSmoothing: "grayscale",
            }}
            data-testid="spyradio-single-root"
        >
            {/* Top strap — same brand as the multi page, with a back link. */}
            <header
                style={{
                    padding: "40px 6vw 24px",
                    borderBottom: `1px solid ${COLOURS.line}`,
                }}
            >
                <Link
                    to="/"
                    data-testid="spyradio-back"
                    style={{
                        display: "inline-block",
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                        fontSize: 11,
                        letterSpacing: "0.28em",
                        textTransform: "uppercase",
                        color: COLOURS.accent,
                        textDecoration: "none",
                        marginBottom: 12,
                    }}
                >
                    ← All stations
                </Link>
                <h1
                    style={{
                        fontFamily: "'Lexend', system-ui, sans-serif",
                        fontSize: "clamp(36px, 5vw, 64px)",
                        fontWeight: 700,
                        letterSpacing: "-0.03em",
                        lineHeight: 1.0,
                        margin: "8px 0 0",
                    }}
                    data-testid="spyradio-single-title"
                >
                    {station?.name || slug || "SPY / RADIO"}
                </h1>
            </header>

            {/* Player */}
            <main
                style={{
                    maxWidth: 1080,
                    margin: "0 auto",
                    padding: "40px 6vw 40px",
                }}
                data-testid="spyradio-single-player"
            >
                {err ? (
                    <div style={{
                        color: "#e5484d", fontSize: 13,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    }}>
                        // {err}
                    </div>
                ) : station ? (
                    <>
                        <PlayerPane
                            station={station}
                            tracks={tracks}
                            loading={tracksLoading}
                        />
                        {/* Iter 31.307 — up-next now rendered inside PlayerPane. */}
                    </>
                ) : (
                    <div style={{
                        color: COLOURS.inkSoft, fontSize: 13,
                        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                    }}>
                        Loading station…
                    </div>
                )}
            </main>

            {/* Sibling stations — nudge to explore the rest of the dial. */}
            {siblings.length > 0 ? (
                <section
                    style={{
                        padding: "24px 6vw 80px",
                        borderTop: `1px solid ${COLOURS.line}`,
                    }}
                    data-testid="spyradio-single-siblings"
                >
                    <div
                        style={{
                            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                            fontSize: 10,
                            letterSpacing: "0.24em",
                            textTransform: "uppercase",
                            color: COLOURS.accent,
                            marginBottom: 16,
                        }}
                    >
                        Also on the dial · {siblings.length}
                    </div>
                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: 12,
                    }}>
                        {siblings.map((s) => (
                            <Link
                                key={s.slug}
                                to={`/s/${encodeURIComponent(s.slug)}`}
                                style={{ textDecoration: "none", color: "inherit" }}
                                data-testid={`spyradio-sibling-link-${s.slug}`}
                            >
                                <StationCard
                                    station={s}
                                    active={false}
                                    onPick={() => { /* navigation handled by Link */ }}
                                />
                            </Link>
                        ))}
                    </div>
                </section>
            ) : null}
        </div>
    );
}
